import { useContext, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Navigate, useNavigate, useParams } from 'react-router';

import { Heading1, Heading2 } from '@components/commons';
import Gallery from '@components/Gallery';
import GalleryFilters, {
  ColorFilter,
} from '@components/Gallery/GalleryFilters';
import { Photo } from '@types';
import {
  formatLocation,
  formatPhotoName,
  formatStartEndDates,
} from '@utils/formatters';
import { getStartEndDates } from '@utils/getters';
import { DataContext } from '@utils/useData';
import { IntlContext } from '@utils/useIntl';

interface ProjectProps {
  baseRoute: string;
}

const Project = ({ baseRoute }: ProjectProps) => {
  const navigate = useNavigate();
  const { seriesId, photoId, zone } = useParams();

  const { photos, series } = useContext(DataContext);
  const { locale, translateTrad } = useContext(IntlContext);

  const project = series.find((project) => project.id === seriesId);
  if (!project) return <Navigate to={baseRoute} />;

  const [fromBase, setFromBase] = useState<boolean>();
  const [colorFilter, setColorFilter] = useState<ColorFilter>();
  const [groupBy, setGroupBy] = useState(project.groupBy);

  const projectName = translateTrad(project.name);
  const projectPhotos = photos.filter(({ seriesIds }) =>
    seriesIds?.includes(project.id),
  );
  const { start, end } = getStartEndDates(projectPhotos);

  const hasColorAndBnw =
    projectPhotos.some(({ color }) => !!color) &&
    photos.some(({ color }) => !color);

  const selectedPhoto = photoId
    ? projectPhotos.find(({ id }) => photoId === id)
    : undefined;

  const photosPerLocation = projectPhotos.reduce<Record<string, Photo[]>>(
    (data, curr) => {
      if (!curr.location) return data;
      const location =
        typeof curr.location === 'string'
          ? curr.location
          : formatLocation(curr.location, true);
      if (data[location]) {
        data[location].push(curr);
      } else {
        data[location] = [curr];
      }
      return data;
    },
    {},
  );

  const photosPerYear = projectPhotos.reduce<Record<string, Photo[]>>(
    (data, curr) => {
      const year = new Date(curr.date).getFullYear();
      if (data[year]) {
        data[year].push(curr);
      } else {
        data[year] = [curr];
      }
      return data;
    },
    {},
  );

  const groupedPhotos =
    groupBy === 'location'
      ? photosPerLocation
      : groupBy === 'year'
        ? photosPerYear
        : { all: projectPhotos };
  const groups =
    groupBy === 'year'
      ? Object.keys(groupedPhotos).sort().reverse()
      : Object.keys(groupedPhotos).sort();

  const onSlideChange = (id?: string) => {
    const fullBaseRoute = zone ? `${baseRoute}/${zone}` : baseRoute;
    if (id) {
      if (!photoId) {
        setFromBase(true);
      }
      navigate(`${fullBaseRoute}/${seriesId}/${id}`, { replace: !!photoId });
    } else {
      fromBase
        ? navigate(-1)
        : navigate(`${fullBaseRoute}/${seriesId}`, { replace: true });
    }
  };

  return (
    <>
      <Helmet>
        <title>
          Marc Malignan | {projectName}
          {selectedPhoto ? ` | ${formatPhotoName(selectedPhoto)}` : ''}
        </title>
      </Helmet>
      {groups.map((group, index) => {
        const groupPhotos = groupedPhotos[group];

        return (
          <Gallery
            key={index === 0 ? 'header-group' : group}
            className={index < groups.length - 1 ? 'mb-8' : undefined}
            photos={groupPhotos}
            onSlideChange={onSlideChange}
            sortType={project.sortType}
            title={
              <>
                {index === 0 && (
                  <Heading1 className="mb-4">{projectName}</Heading1>
                )}
                {index === 0 && project.description && (
                  <p className="mb-4">{project.description}</p>
                )}
              </>
            }
            subTitle={
              <Heading2>
                {groupBy ? group : formatStartEndDates(start, end, locale)}
              </Heading2>
            }
            filters={
              index === 0 ? (
                <GalleryFilters
                  colorFilter={colorFilter}
                  setColorFilter={setColorFilter}
                  groupBy={groupBy}
                  setGroupBy={setGroupBy}
                  hasColorAndBnw={hasColorAndBnw}
                  hasMultipleLocations={
                    Object.keys(photosPerLocation).length > 1
                  }
                  hasMultipleYears={Object.keys(photosPerYear).length > 1}
                />
              ) : null
            }
            colorFilter={colorFilter}
          />
        );
      })}
    </>
  );
};

export default Project;
